/**
 * AxLabs Experiments - Population Dynamics
 *
 * Demonstrates population growth models including exponential,
 * logistic, and Lotka-Volterra predator-prey dynamics.
 *
 * Governing equations:
 * - Exponential: dN/dt = rN
 * - Logistic: dN/dt = rN(1 - N/K)
 * - Lotka-Volterra: dPrey/dt = αP - βPQ, dPred/dt = δPQ - γQ
 */
import { BaseSimulationEngine, DIMENSIONS, rk4StepNumeric, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'modelType',
        name: 'Model Type',
        symbol: '',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 1,
        max: 3,
        default: 2,
        step: 1,
        description: 'Growth model: 1=Exponential, 2=Logistic, 3=Predator-Prey',
    },
    {
        id: 'initialPrey',
        name: 'Initial Population (Prey)',
        symbol: 'N_0',
        unit: 'individuals',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 1,
        max: 10000,
        default: 100,
        step: 10,
        description: 'Starting population of prey (or single species)',
    },
    {
        id: 'initialPredator',
        name: 'Initial Predator Population',
        symbol: 'P_0',
        unit: 'individuals',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 1000,
        default: 20,
        step: 5,
        description: 'Starting population of predators',
    },
    {
        id: 'growthRate',
        name: 'Intrinsic Growth Rate',
        symbol: 'r',
        unit: '1/time',
        dimension: DIMENSIONS.FREQUENCY,
        min: 0.01,
        max: 2,
        default: 0.5,
        step: 0.05,
        description: 'Maximum per-capita growth rate',
    },
    {
        id: 'carryingCapacity',
        name: 'Carrying Capacity',
        symbol: 'K',
        unit: 'individuals',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 10,
        max: 100000,
        default: 1000,
        step: 100,
        description: 'Maximum sustainable population',
    },
    {
        id: 'predationRate',
        name: 'Predation Rate',
        symbol: '\\beta',
        unit: '1/(pred·time)',
        dimension: DIMENSIONS.FREQUENCY,
        min: 0.001,
        max: 0.1,
        default: 0.01,
        step: 0.001,
        description: 'Rate at which predators kill prey',
    },
    {
        id: 'predatorDeathRate',
        name: 'Predator Death Rate',
        symbol: '\\gamma',
        unit: '1/time',
        dimension: DIMENSIONS.FREQUENCY,
        min: 0.01,
        max: 2,
        default: 0.3,
        step: 0.05,
        description: 'Predator mortality rate without prey',
    },
    {
        id: 'conversionEfficiency',
        name: 'Conversion Efficiency',
        symbol: '\\delta',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0.001,
        max: 0.5,
        default: 0.02,
        step: 0.005,
        description: 'Efficiency of converting prey to predator offspring',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'exponential',
        name: 'Exponential Growth',
        latex: '\\frac{dN}{dt} = rN',
        variables: ['prey', 'growthRate'],
        description: 'Unlimited growth at constant per-capita rate',
        condition: (params) => (params['modelType'] ?? 2) === 1,
    },
    {
        id: 'logistic',
        name: 'Logistic Growth',
        latex: '\\frac{dN}{dt} = rN\\left(1 - \\frac{N}{K}\\right)',
        variables: ['prey', 'growthRate', 'carryingCapacity'],
        description: 'Growth limited by carrying capacity',
        condition: (params) => (params['modelType'] ?? 2) === 2,
    },
    {
        id: 'lotka_volterra_prey',
        name: 'Prey Dynamics (Lotka-Volterra)',
        latex: '\\frac{dN}{dt} = rN - \\beta NP',
        variables: ['prey', 'growthRate', 'predationRate', 'predator'],
        description: 'Prey growth reduced by predation',
        condition: (params) => (params['modelType'] ?? 2) === 3,
    },
    {
        id: 'lotka_volterra_pred',
        name: 'Predator Dynamics (Lotka-Volterra)',
        latex: '\\frac{dP}{dt} = \\delta\\beta NP - \\gamma P',
        variables: ['predator', 'conversionEfficiency', 'predationRate', 'prey', 'predatorDeathRate'],
        description: 'Predator growth depends on prey availability',
        condition: (params) => (params['modelType'] ?? 2) === 3,
    },
    {
        id: 'equilibrium_prey',
        name: 'Prey Equilibrium',
        latex: 'N^* = \\frac{\\gamma}{\\delta\\beta}',
        variables: ['predatorDeathRate', 'conversionEfficiency', 'predationRate'],
        description: 'Stable prey population in predator-prey equilibrium',
        condition: (params) => (params['modelType'] ?? 2) === 3,
    },
    {
        id: 'equilibrium_pred',
        name: 'Predator Equilibrium',
        latex: 'P^* = \\frac{r}{\\beta}',
        variables: ['growthRate', 'predationRate'],
        description: 'Stable predator population in predator-prey equilibrium',
        condition: (params) => (params['modelType'] ?? 2) === 3,
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'extinction',
        name: 'Population Extinction',
        description: 'Population has dropped to zero',
        check: (state) => (state.variables['prey'] ?? 1) < 1,
        severity: 'warning',
        educationalMessage: 'The population has gone extinct. In nature, this can happen when death rate exceeds birth rate ' +
            'or predation is too intense.',
    },
    {
        id: 'predator_extinction',
        name: 'Predator Extinction',
        description: 'Predator population has dropped to zero',
        check: (state, params) => {
            const modelType = params['modelType'] ?? 2;
            return modelType === 3 && (state.variables['predator'] ?? 1) < 1;
        },
        severity: 'warning',
        educationalMessage: 'The predator population has gone extinct. Without predators, prey may grow to carrying capacity.',
    },
    {
        id: 'overshoot',
        name: 'Population Overshoot',
        description: 'Population has exceeded carrying capacity significantly',
        check: (state, params) => {
            const K = params['carryingCapacity'] ?? 1000;
            return (state.variables['prey'] ?? 0) > 1.5 * K;
        },
        severity: 'warning',
        educationalMessage: 'The population has significantly overshot the carrying capacity. ' +
            'This often leads to a population crash as resources are depleted.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const POPULATION_DYNAMICS_CONFIG = {
    id: 'population-dynamics',
    slug: 'population-dynamics',
    name: 'Population Dynamics',
    domain: 'biology',
    description: 'Explore population growth and predator-prey interactions',
    longDescription: `
Population dynamics models how populations change over time based on birth, death,
and interactions with other species.

Models included:
1. Exponential Growth: Unlimited resources, constant per-capita rate
2. Logistic Growth: Growth limited by carrying capacity
3. Lotka-Volterra: Classic predator-prey oscillations

Key concepts:
- Intrinsic growth rate (r): Maximum per-capita growth
- Carrying capacity (K): Maximum sustainable population
- Predator-prey cycles: Linked oscillations with phase lag
- Equilibrium: Stable population levels

Ecological insights:
- Exponential growth is unsustainable in real ecosystems
- Carrying capacity depends on resource availability
- Predator-prey systems often show oscillations
- Removing predators can cause prey overshoot and crash
  `.trim(),
    version: 1,
    conceptTags: ['ecology', 'population', 'growth', 'predator-prey', 'logistic'],
    difficulty: 4,
    prerequisites: ['differential-equations', 'ecology-basics'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'prey', type: 'parameter', parameterId: 'initialPrey' },
        { variableId: 'predator', type: 'parameter', parameterId: 'initialPredator' },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 0.05,
    maxDt: 0.5,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class PopulationDynamicsSimulation extends BaseSimulationEngine {
    config = POPULATION_DYNAMICS_CONFIG;
    constructor() {
        super({ prey: 100, predator: 20, preyGrowthRate: 0, predatorGrowthRate: 0 }, { type: 'rk4', dt: 0.05, maxSubsteps: 10 });
    }
    getInitialState(params) {
        const prey0 = params['initialPrey'] ?? 100;
        const pred0 = params['initialPredator'] ?? 20;
        return {
            prey: prey0,
            predator: pred0,
            preyGrowthRate: 0,
            predatorGrowthRate: 0,
        };
    }
    computeDerivatives(state, params) {
        const modelType = params['modelType'] ?? 2;
        const r = params['growthRate'] ?? 0.5;
        const K = params['carryingCapacity'] ?? 1000;
        const beta = params['predationRate'] ?? 0.01;
        const gamma = params['predatorDeathRate'] ?? 0.3;
        const delta = params['conversionEfficiency'] ?? 0.02;
        const N = state.prey;
        const P = state.predator;
        let dN = 0;
        let dP = 0;
        switch (modelType) {
            case 1: // Exponential
                dN = r * N;
                dP = 0;
                break;
            case 2: // Logistic
                dN = r * N * (1 - N / K);
                dP = 0;
                break;
            case 3: // Lotka-Volterra
                dN = r * N - beta * N * P;
                dP = delta * beta * N * P - gamma * P;
                break;
        }
        return {
            prey: dN,
            predator: dP,
        };
    }
    integrate(dt) {
        const derivative = (s, _t) => {
            const d = this.computeDerivatives(s, this.parameters);
            return {
                prey: d.prey ?? 0,
                predator: d.predator ?? 0,
                preyGrowthRate: 0,
                predatorGrowthRate: 0,
            };
        };
        const newState = rk4StepNumeric(this.state, derivative, this.time, dt);
        // Ensure non-negative populations
        this.state.prey = Math.max(0, newState.prey ?? 0);
        this.state.predator = Math.max(0, newState.predator ?? 0);
        // Update growth rates
        const derivs = this.computeDerivatives(this.state, this.parameters);
        this.state.preyGrowthRate = derivs.prey ?? 0;
        this.state.predatorGrowthRate = derivs.predator ?? 0;
    }
    getPositionForStabilityCheck() {
        return this.state.prey;
    }
    /**
     * Calculate equilibrium populations for predator-prey model
     */
    getEquilibriumPopulations() {
        const modelType = this.parameters['modelType'] ?? 2;
        if (modelType !== 3) {
            return null;
        }
        const r = this.parameters['growthRate'] ?? 0.5;
        const beta = this.parameters['predationRate'] ?? 0.01;
        const gamma = this.parameters['predatorDeathRate'] ?? 0.3;
        const delta = this.parameters['conversionEfficiency'] ?? 0.02;
        // N* = γ / (δβ)
        const preyEq = gamma / (delta * beta);
        // P* = r / β
        const predatorEq = r / beta;
        return { prey: preyEq, predator: predatorEq };
    }
    /**
     * Get the logistic carrying capacity progress
     */
    getCarryingCapacityFraction() {
        const K = this.parameters['carryingCapacity'] ?? 1000;
        return this.state.prey / K;
    }
}
/**
 * Factory function to create a new population dynamics simulation
 */
export function createPopulationDynamics() {
    return new PopulationDynamicsSimulation();
}
//# sourceMappingURL=population-dynamics.js.map